import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, Loader2, Pencil, Send, Settings, ShieldCheck, Trash2, XCircle } from 'lucide-react';
import { useCRM, useCRMCollections } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import {
  SURETY_BOND_REQUEST_STATUS_LABELS,
  SURETY_BOND_TYPE_LABELS,
  type SuretyBondRequest,
  type SuretyBondRequestStatus,
  type SuretyBondingSettings,
} from '../../types/regulatory';
import {
  deleteSuretyBondRequest,
  saveSuretyBondRequest,
  saveSuretyBondingSettings,
  setSuretyBondRequestStatus,
  subscribeSuretyBondRequests,
  subscribeSuretyBondingSettings,
} from '../../utils/suretyBondRequestsService';
import {
  DEFAULT_SURETY_BONDING_SETTINGS,
  activeContractRange,
  formatBondMoney,
  formatBondMoneyRange,
  longDateFromYmd,
  type SuretyBondRequestDraft,
} from '../../utils/suretyBondRequest';
import { needsScheduleBeforeDownload } from '../../utils/suretyEnclosures';
import { mirrorSuretyBondToPortal } from '../../utils/suretyBondPortalMirror';
import SuretyBondRequestModal from './SuretyBondRequestModal';
import SuretyBondingSettingsModal from './SuretyBondingSettingsModal';
import './RegulatorySection.css';

interface RegulatorySectionProps {
  readOnly: boolean;
  /** Bumped by the hub toolbar's "New bond request" button. */
  newRequestNonce: number;
}

type StatusFilter = 'all' | SuretyBondRequestStatus;

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Draft' },
  { id: 'sent', label: 'Sent' },
  { id: 'issued', label: 'Issued' },
  { id: 'declined', label: 'Declined' },
];

const RegulatorySection: React.FC<RegulatorySectionProps> = ({ readOnly, newRequestNonce }) => {
  const { user } = useAuth();
  const { enhancedDeals = [], timberProjects = [], projectTasks = [], taskGroups = [] } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups']);

  const [rows, setRows] = useState<SuretyBondRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<SuretyBondingSettings>(DEFAULT_SURETY_BONDING_SETTINGS);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [editing, setEditing] = useState<SuretyBondRequest | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [autoDownload, setAutoDownload] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribeSuretyBondRequests(
      (next) => {
        setRows(next);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Failed to load bond requests');
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeSuretyBondingSettings(
      (next) => setSettings(next),
      (err) => setError(err.message || 'Failed to load bonding defaults'),
    );
    return unsub;
  }, []);

  useEffect(() => {
    if (newRequestNonce > 0 && !readOnly) {
      setEditing(null);
      setShowRequestModal(true);
    }
  }, [newRequestNonce, readOnly]);

  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: rows.length, draft: 0, sent: 0, issued: 0, declined: 0 };
    rows.forEach((r) => { c[r.status] += 1; });
    return c;
  }, [rows]);

  const download = useCallback(async (request: SuretyBondRequestDraft & Partial<Pick<SuretyBondRequest, 'id'>>) => {
    const { exportSuretyBondRequestPdf } = await import('../../utils/suretyBondRequestReport');
    await exportSuretyBondRequestPdf(request);
  }, []);

  const handleDownload = async (row: SuretyBondRequest) => {
    // The schedule enclosure is listed but was never built — open the request
    // with the wizard queued so the page is generated and included in the PDF.
    if (!readOnly && needsScheduleBeforeDownload(row)) {
      setEditing(row);
      setAutoDownload(true);
      setShowRequestModal(true);
      return;
    }
    setBusyId(row.id);
    setError(null);
    try {
      await download(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build the PDF');
    } finally {
      setBusyId(null);
    }
  };

  const [portalNotice, setPortalNotice] = useState<string | null>(null);

  /**
   * Push the client-safe bond status onto the portal for this deal. Uses the
   * rows we already have plus the request that just changed (the snapshot may
   * not have caught up yet). Portal problems are a notice, not a failure —
   * the request itself is already saved.
   */
  const syncPortal = useCallback(async (changed: SuretyBondRequest) => {
    const forDeal = rows.filter((r) => r.dealId === changed.dealId && r.id !== changed.id).concat(changed);
    try {
      const found = await mirrorSuretyBondToPortal(changed.dealId, forDeal);
      setPortalNotice(found ? null : `${changed.projectLabel || 'This project'} has no client portal yet — bond status will show once a portal exists.`);
    } catch (err) {
      setPortalNotice(`Saved, but the client portal could not be updated: ${err instanceof Error ? err.message : 'unknown error'}`);
    }
  }, [rows]);

  const handleSave = async (draft: SuretyBondRequestDraft, opts: { download: boolean }) => {
    const userId = user?.id || '';
    const id = await saveSuretyBondRequest(draft, { id: editing?.id, userId });
    if (opts.download) {
      await download({ ...draft, id });
    }
    void syncPortal({
      ...draft,
      id,
      createdAt: editing?.createdAt ?? new Date(),
      createdBy: editing?.createdBy ?? userId,
      updatedAt: new Date(),
      updatedBy: userId,
      sentAt: editing?.sentAt ?? null,
      issuedAt: editing?.issuedAt ?? null,
    });
  };

  const handleStatus = async (row: SuretyBondRequest, status: SuretyBondRequestStatus) => {
    const userId = user?.id || '';
    let bondNumber: string | undefined;
    let suretyName: string | undefined;
    if (status === 'issued') {
      const num = window.prompt('Bond number (optional)', row.bondNumber || '');
      if (num === null) return;
      const surety = window.prompt('Surety company (optional)', row.suretyName || '');
      if (surety === null) return;
      bondNumber = num.trim();
      suretyName = surety.trim();
    }
    if (status === 'declined' && !window.confirm('Mark this bond request as declined?')) return;
    setBusyId(row.id);
    setError(null);
    try {
      await setSuretyBondRequestStatus(row.id, status, { userId, bondNumber, suretyName });
      const now = new Date();
      void syncPortal({
        ...row,
        status,
        bondNumber: bondNumber ?? row.bondNumber,
        suretyName: suretyName ?? row.suretyName,
        sentAt: status === 'sent' ? now : row.sentAt,
        issuedAt: status === 'issued' ? now : row.issuedAt,
        updatedAt: now,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row: SuretyBondRequest) => {
    const label = `${row.projectLabel || 'this project'} — ${SURETY_BOND_TYPE_LABELS[row.bondType] || 'bond'} (${SURETY_BOND_REQUEST_STATUS_LABELS[row.status] || row.status})`;
    const warn = row.status === 'issued'
      ? '\n\nThis bond is marked issued. Deleting removes the record from the CRM and the client portal; it does not cancel the bond with the surety.'
      : '';
    if (!window.confirm(`Delete the bond request for ${label}?${warn}\n\nThis cannot be undone.`)) return;
    setBusyId(row.id);
    setError(null);
    try {
      await deleteSuretyBondRequest(row.id);
      // Re-mirror the portal from the remaining requests on this deal so the
      // client stops seeing the deleted one.
      const remaining = rows.filter((r) => r.dealId === row.dealId && r.id !== row.id);
      try {
        await mirrorSuretyBondToPortal(row.dealId, remaining);
        setPortalNotice(null);
      } catch (err) {
        setPortalNotice(`Deleted, but the client portal could not be updated: ${err instanceof Error ? err.message : 'unknown error'}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete bond request';
      setError(/permission/i.test(msg) ? 'You do not have permission to delete bond requests.' : msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveSettings = async (next: SuretyBondingSettings) => {
    await saveSuretyBondingSettings(next, user?.id || '');
  };

  const agentLabel = settings.agent.agency || settings.agent.contact;

  return (
    <div className="reg">
      {error && <div className="reg-error">{error}</div>}
      {portalNotice && (
        <div className="reg-notice" role="status">
          {portalNotice}
          <button type="button" className="reg-notice-dismiss" aria-label="Dismiss" onClick={() => setPortalNotice(null)}>×</button>
        </div>
      )}

      <div className="reg-card">
        <div className="reg-card-head">
          <div>
            <h3><ShieldCheck size={14} /> Surety bond requests</h3>
            <p className="reg-card-hint">
              Request letters to the bond agent for bid bonds and performance &amp; payment bonds.
              Each letter prefills from the project, saves here with its status, and downloads as a PDF
              on Leiper&apos;s Creek Timber Frames letterhead. The status is mirrored to the client&apos;s
              portal so they can follow bonding progress.
            </p>
          </div>
          <div className="reg-card-actions">
            <span className={`reg-agent-pill${agentLabel ? '' : ' is-missing'}`}>
              {agentLabel ? `Agent: ${agentLabel}` : 'No bond agent saved'}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="fin-icon-btn"
                aria-label="Surety bonding defaults"
                title="Bond agent, signer, and defaults"
                onClick={() => setShowSettings(true)}
              >
                <Settings size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="reg-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`reg-filter${filter === f.id ? ' active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label} · {counts[f.id]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="reg-loading"><Loader2 size={14} className="reg-spin" /> Loading bond requests…</div>
        ) : filtered.length === 0 ? (
          <div className="reg-empty">
            <strong>{rows.length === 0 ? 'No bond requests yet' : 'Nothing in this status'}</strong>
            {rows.length === 0
              ? readOnly
                ? 'Requests created by the office will appear here.'
                : 'Use “New bond request” above to start a letter for a project.'
              : 'Pick another filter to see the rest.'}
          </div>
        ) : (
          <div className="fin-table-wrap">
            <table className="reg-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Bond</th>
                  <th className="is-right">Bond amount</th>
                  <th>Status</th>
                  <th>Letter date</th>
                  <th>Needed by</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const busy = busyId === row.id;
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="reg-project">
                          <strong>{row.projectLabel || row.projectNumber || 'Project'}</strong>
                          <span className="is-muted">
                            {row.obligee.name ? `Obligee: ${row.obligee.name}` : 'Obligee not set'}
                          </span>
                        </div>
                      </td>
                      <td>
                        {SURETY_BOND_TYPE_LABELS[row.bondType]}
                        <div className="is-muted">
                          {row.bondPercent}% of {(() => {
                            const r = activeContractRange(row);
                            return r ? `${formatBondMoneyRange(r)} (range)` : formatBondMoney(row.contractAmount);
                          })()}
                        </div>
                      </td>
                      <td className="is-right">{formatBondMoney(row.bondAmount)}</td>
                      <td>
                        <span className={`reg-status ${row.status}`}>
                          {SURETY_BOND_REQUEST_STATUS_LABELS[row.status]}
                        </span>
                        {row.status === 'issued' && (row.bondNumber || row.suretyName) && (
                          <div className="is-muted">
                            {[row.suretyName, row.bondNumber ? `#${row.bondNumber}` : ''].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td>{longDateFromYmd(row.letterDate)}</td>
                      <td>{longDateFromYmd(row.neededBy)}</td>
                      <td>
                        <div className="reg-row-actions">
                          <button
                            type="button"
                            className="fin-icon-btn"
                            aria-label="Download PDF"
                            title="Download PDF"
                            disabled={busy}
                            onClick={() => handleDownload(row)}
                          >
                            {busy ? <Loader2 size={14} className="reg-spin" /> : <Download size={14} />}
                          </button>
                          {!readOnly && (
                            <>
                              <button
                                type="button"
                                className="fin-icon-btn"
                                aria-label="Edit"
                                title="Edit"
                                disabled={busy}
                                onClick={() => { setEditing(row); setShowRequestModal(true); }}
                              >
                                <Pencil size={14} />
                              </button>
                              {row.status === 'draft' && (
                                <button
                                  type="button"
                                  className="fin-icon-btn"
                                  aria-label="Mark sent to agent"
                                  title="Mark sent to agent"
                                  disabled={busy}
                                  onClick={() => handleStatus(row, 'sent')}
                                >
                                  <Send size={14} />
                                </button>
                              )}
                              {(row.status === 'draft' || row.status === 'sent') && (
                                <>
                                  <button
                                    type="button"
                                    className="fin-icon-btn"
                                    aria-label="Mark bond issued"
                                    title="Mark bond issued"
                                    disabled={busy}
                                    onClick={() => handleStatus(row, 'issued')}
                                  >
                                    <CheckCircle2 size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="fin-icon-btn danger"
                                    aria-label="Mark declined"
                                    title="Mark declined"
                                    disabled={busy}
                                    onClick={() => handleStatus(row, 'declined')}
                                  >
                                    <XCircle size={14} />
                                  </button>
                                </>
                              )}
                              {row.status === 'declined' && (
                                <button
                                  type="button"
                                  className="fin-icon-btn"
                                  aria-label="Reopen as draft"
                                  title="Reopen as draft"
                                  disabled={busy}
                                  onClick={() => handleStatus(row, 'draft')}
                                >
                                  <Pencil size={14} />
                                </button>
                              )}
                              <button
                                type="button"
                                className="fin-icon-btn danger"
                                aria-label="Delete bond request"
                                title="Delete bond request"
                                disabled={busy}
                                onClick={() => handleDelete(row)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRequestModal && !readOnly && (
        <SuretyBondRequestModal
          existing={editing}
          deals={enhancedDeals}
          timberProjects={timberProjects}
          scheduleTasks={projectTasks}
          taskGroups={taskGroups}
          settings={settings}
          autoDownload={autoDownload}
          onClose={() => { setShowRequestModal(false); setEditing(null); setAutoDownload(false); }}
          onSave={handleSave}
        />
      )}

      {showSettings && !readOnly && (
        <SuretyBondingSettingsModal
          initial={settings}
          onClose={() => setShowSettings(false)}
          onSave={handleSaveSettings}
        />
      )}
    </div>
  );
};

export default RegulatorySection;
